//============================================================================
// Name        : types.ts
// Purpose     : Define manufacturing module types
// Created     : 2019-07-10
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

import {jsx} from "@emotion/core";
import Lot from "../../../manufacturing/Lot";
import ConfirmDialog from "./ConfirmDialog";

//============================================================================
// Type Aliases
//============================================================================

export type ChangeInputEvent    = React.ChangeEvent<HTMLInputElement>;
export type ChangeTextAreaEvent = React.ChangeEvent<HTMLTextAreaElement>;
export type ChangeSelectEvent   = React.ChangeEvent<HTMLSelectElement>;

//============================================================================
// Redux Store
//============================================================================

export interface IPrinterModel {
    id: number;
    name: string;
}

export interface IPrinterOption {
    value: string;
    label: string;
}

export interface IEPCEncodingInfo {
    prefix: string;
    itemLength: number;
    serialLength: number;
    palletLength: number;
    caseLength: number;
}

export interface IPrintState {
    items: IPrinterOption[];
    savePrinter: boolean;
    defaultPrinter: number;
    selectedPrinter: IPrinterOption;

    palletCount: number;
    caseCount: number;
    detectedPalletCount: number;
    detectedCaseCount: number;
    printing: boolean;
}

//============================================================================
// Redux Store Actions
//============================================================================
export enum ReceiveActions {
    UpdateState = "UpdateState",
    StoreReceiveProps = "StoreReceiveProps",
    Continue = "Continue",
    BeginReception = "BeginReception",
    FetchItemInfo = "FetchItemInfo",
    UpdateLot = "UpdateLot",
    AddLot = "AddLot",
    RemoveLot = "RemoveLot",
    ShowCancelConfirmation = "ShowCancelConfirmation",
    HideCancelConfirmation = "HideCancelConfirmation",
    CancelReceive = "CancelReceive",
    HideErrorDialog = "HideErrorDialog",
    ShowCancelItemConfirmation = "ShowCancelItemConfirmation",
    CancelReceiveItem = "CancelReceiveItem",
    HideCancelItemConfirmation = "HideCancelItemConfirmation",
    PrintLabels = "PrintLabels",
    ConfirmCancelPrint = "ConfirmCancelPrint",
    BeginPrinting = "BeginPrinting",
    HideCancelPrint = "HideCancelPrint",
    CancelPrinting = "CancelPrinting",
    SaveGeneratedLabels = "SaveGeneratedLabels",
    ShowErrorDialog = "ShowErrorDialog",
    SetErrorDismissCallback = "SetErrorDismissCallback",
    SetItemStorageUnits = "SetItemStorageUnits",
    SetLabelVerified = "SetLabelVerified",
    FinishReceivingItem = "FinishReceivingItem",
    FinishReception = "FinishReception",
    ShowUndoItemConfirmation = "ShowUndoItemConfirmation",
    ShowReprintDialog = "ShowReprintDialog",
    HideReprintDialog = "HideReprintDialog",
    ShowChangeDateConfirmation = "ShowChangeDateConfirmation",
    ShowUndoProcess = "ShowUndoProcess"
}

export enum ReceiveItemActions {
    UpdateState = "UpdateState",
    SetItemReceiving = "SetItemReceiving",
    UndoReceiving = "UndoReceiving",
    FetchItemInfo = "FetchItemInfo",
    StartReceivingItem = "StartReceivingItem"
}

export enum PrinterActions {
    FetchPrinters = "FetchPrinters",
    SetSavePrinter = "SetSavePrinter",
    SaveDefaultPrinter = "SaveDefaultPrinter",
    IncrementLastSerial = "IncrementLastSerial",
    ShowReprintDialog = "ShowReprintDialog",
    CloseReprintDialog = "CloseReprintDialog",
    SetSelectedPrinter = "SetSelectedPrinter",
    SetPrintState = "SetPrintState"
}

//============================================================================
// Error Dialog
//============================================================================

export interface IErrorDialogProps {
    show: boolean;
    title: string;
    message: string;
    callback?: () => void;
    hideErrorDialog: () => void;
}

export interface IErrorDialogState {
    show: boolean;
}

//============================================================================
// Confirmation Dialog
//============================================================================

export interface IConfirmDialogProps {
    show: boolean;
    title: string;
    message: string;
    positiveBtnLbl: string;
    negativeBtnLbl: string;
    positiveBtnCallback?: (state?: any) => any;
    negativeBtnCallback?: () => void;
    positiveCallback?: () => void;
    negativeCallback?: () => void;
}

export interface IConfirmDialogState {
    show: boolean;
}

//==============================================================================
//
//==============================================================================

export interface IUndoProcessDialogProps {
    show: boolean;
    states: string[];
    checkTagsLive?: "loading" | "success" | "failed";
    checkTagsQuantities?: "loading" | "success" | "failed";
    checkTagAllocations?: "loading" | "success" | "failed";
    checkNAV?: "loading" | "success" | "failed";
    checkTagsKilled?: "loading" | "success" | "failed";
    error?: any;
}

//============================================================================
// Password Dialog
//============================================================================

export interface IPasswordDialogProps {
    show: boolean;
    positiveBtnCallback: (state?: any) => any;
    negativeBtnCallback?: () => void;
    positiveCallback?: () => void;
}

//============================================================================
// Reception Component
//============================================================================

export interface IReceptionProps {
    searchUrl: string;
    receiveUrl: string;
}

export interface IReceptionState {
    query: string;
    receptionItems: IItem[];
    apiLogs: boolean;
}

//============================================================================
// Receive Component
//============================================================================

export interface ICatalogItem {
    id: number;
    alu: string;
    number: string;
    description: string;
    mf_exp_calculation: string;
}

export interface IItem {
    isReceiving?: boolean;
    external_receive_items: Array<Partial<IReceptionItem>>;

    id: number;
    type_id: string;
    receiving_id: string;
    vendor_id: string;
    vendor_name: string;
    receive_price: number;
    posting_date: string;
    actual_receipt_date: string;
    actual_qty: number;
    expected_receipt_date: number;
    expected_qty: number;
    send_api_time: string;
    lot_id: string;
    pallet_qty: number;
    containers_per_pallet: number;
    pallet_extra_containers: number;
    total_received: number;
    received_by_user_id: number;
    received_by_username: number;
    status?: ReceiveStatuses;
    invoiced: boolean;

    measure_unit_cost?: string;
    average_previous_price?: number;
}

export interface IReceptionItem {
    showConfirmation: boolean;
    isReceiving?: boolean;
    blocked: boolean;
    status: ReceiveStatuses;
    lots: Lot[];
    labels: IPrintLabelItem[];
    catalogItem?: ICatalogItem;
    storageUnits?: IStorageUnit[];
    defaultStorageUnit?: IStorageUnit;
    qtyValid: boolean;

    id: number;
    receiving_id: string;
    item_number: string;
    item_description: string;
    expected_receipt_date: string;
    expected_qty: number;
    storage_unit: string;
    measure_unit: string;
    measure_unit_cost: number;
    unit_weight: number;
    receive_price: number;
    line_uofm_code: string;
}

export interface IReceiveProps {
    reception: Partial<IItem>;
    currentItemId: number;
    printers: IPrinterModel[];
    epcEncodingInfo: IEPCEncodingInfo;
    allowedStartDate: string;
    allowedEndDate: string;
    qtyTolerance: number;
    qtyToleranceUnder: number;
    priceFluctuationThreshold: number;
    invoiced: boolean;

    // Item properties
    postingDate: string;
    validDates: boolean;
    isReceiving: boolean;

    // Virtual Properties
    receivingId: string;
    vendorName: string;
    status: ReceiveStatuses;

    // Confirmation Dialog
    showConfirmation: boolean;
    showItemConfirmation: boolean;
    confirmationTitle: string;
    confirmationMsg: string;
    confirmationDlgPositiveCallback: () => void;
    confirmationDlgNegativeCallback: () => void;
    showUndoProcess: boolean;

    // Error Dialog
    showError: boolean;
    errorTitle: string;
    errorMsg: string;
    dismissErrorCallback: () => void;

    // Printing Dialog
    showPrintDialog: boolean;
    showCancelPrintConfirm: boolean;
    showReprintDialog: boolean;
    validatePasswordUrl: string;

    // Actions
    storeReceiveProps: (props: IReceiveProps) => void;
    updateState: (newState: object) => void;
    continue: () => void;
    cancelReceive: () => void;
    showCancelConfirmation(positiveCb: () => void, negativeCb: () => void): () => void;
    hideCancelConfirmation: () => void;
    showChangeDateConfirmation(positiveCb: () => void, negativeCb: () => void): () => void;

    positiveConfirmationCallback: (state: any) => any;
}

export interface IReceiveState {
    validDates: boolean;
    isReceiving: boolean;
}

export interface IReceiveItemState {
    isReceiving: boolean;
    blocked: boolean;
    status: ReceiveStatuses;

    active: boolean;
    lots: Lot[];
}

export interface IStorageUnit {
    id: number;
    item_number: string;
    name: string;
    measure_unit: string;
}

//============================================================================
// Receive Status Component
//============================================================================

export enum ReceiveStatuses {
    Pending    = "Pending",
    InProgress = "InProgress",
    Complete   = "Complete"
}

export interface IReceiveStatusProps {
    addedClass?: string;
    status: ReceiveStatuses;
}

//============================================================================
// Receive Item Lot Component
//============================================================================

export interface IReceiveItemLotProps {
    itemId: number;
    reception: Partial<IItem>;
    lot: Lot;
    storageUnits: [];
    defaultStorageUnit: IStorageUnit;
    hideCancelConfirmation: () => void;
    showChangeDateConfirmation(positiveCb: () => void, negativeCb: () => void): () => void;

    // Redux Actions
    updateLot: (itemId: number, lotId: number, newState: object) => void;
    removeLot: (itemId: number, lotId: number) => void;
}

//============================================================================
// Print Labels Component
//============================================================================

export interface IPrintLabelsProps {
    show: boolean;
    showCancelConfirm: boolean;
    confirmationTitle: string;
    confirmationMsg: string;
    validatePasswordUrl: string;
    actualReceiptDate: string;
    postingDate: string;

    itemId: number;
    savePrinter: boolean;
    selectedPrinter: IPrinterOption;
    printers: IPrinterOption[];
    printerModels: IPrinterModel[];
    reception: IReceptionItem;
    receptionItems: Array<Partial<IReceptionItem>>;

    // Printing state
    palletCount: number;
    caseCount: number;
    detectedPalletCount: number;
    detectedCaseCount: number;
    printing: boolean;

    fetchPrinters: (printers: IPrinterModel[]) => void;
    setSavePrinter: (value: boolean) => void;
    saveDefaultPrinter?: (option: IPrinterOption) => void;
    setSelectedPrinter?: (option: IPrinterOption) => void;
    setPrintState: (props: Partial<IPrintState>) => void;
    setLabelVerified: (itemId: number, epc: string) => void;
    finishReceivingItem: (props: object) => void;

    showConfirmCancelPrint: () => void;
    hideCancelConfirmation: () => void;
    cancelPrinting: () => void;
    closeCallback?: () => void;
    showReprintDialog?: () => void;
    closeReprintDialog?: () => void;
}

export interface IReprintLabelsProps {
    show: boolean;

    itemId: number;
    savePrinter: boolean;
    selectedPrinter: IPrinterOption;
    printers: IPrinterOption[];
    printerModels: IPrinterModel[];
    reception: IReceptionItem;
    receptionItems: Array<Partial<IReceptionItem>>;

    // Printing state
    palletCount: number;
    caseCount: number;
    detectedPalletCount: number;
    detectedCaseCount: number;
    printing: boolean;

    setSavePrinter: (value: boolean) => void;
    saveDefaultPrinter?: (option: IPrinterOption) => void;
    setSelectedPrinter?: (option: IPrinterOption) => void;
    setPrintState: (props: Partial<IPrintState>) => void;
    hideReprintDialog: () => void;
}

export interface IReprintItemProps {
    label: IPrintLabelItem;
    selectedPrinter: IPrinterOption;
}

//============================================================================

export enum TagType {
    Pallet = "Pallet",
    Case   = "Case"
}

export interface IPrintLabelItem {
    // Original input
    inputQty: number;
    inputUom: string;

    individualCase: boolean;
    itemDescription: string;
    epc: string;
    itemNumber: string;
    itemAlu: string;
    itemExpDate: string;
    receivingId: string;
    receivingItemId: number;
    lotIdx: number;
    lotCount: number;
    palletCases: number;
    storageUnit: string;
    selectedStorageUnit: IStorageUnit;
    qtyInOrigStorageUnit: number;
    missingExpDate: boolean;

    // Asset fields
    serialNumber: number;
    lotNumber: string;
    lotExpDate: string;
    tagType: TagType;
    orgUnits: number;
    currentUnits: number;
    costPerStorageUnit: number;
    costPerContainer: number;
    palletNumber: number;
    caseNumber: number;

    // Label count
    currentLabelNum: number;
    ofTotalLabelCount: number;
    verified: boolean;
}
